// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect, useRef, useCallback } from 'react'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', brown: '#2C1D14', muted: '#9A7B68',
}

// Singleton script loader — only loads once across all instances
let _scriptPromise = null
function loadGoogleScript(apiKey) {
  if (window.google?.maps?.places) return Promise.resolve()
  if (_scriptPromise) return _scriptPromise
  _scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script')
    el.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`
    el.async = true
    el.onload = resolve
    el.onerror = () => { _scriptPromise = null; reject(new Error('Google Places failed to load')) }
    document.head.appendChild(el)
  })
  return _scriptPromise
}

function parseAddressComponents(components) {
  let streetNumber = '', route = '', city = '', state = '', zip = ''
  for (const c of components) {
    const t = c.types[0]
    if (t === 'street_number') streetNumber = c.long_name
    else if (t === 'route') route = c.long_name
    else if (t === 'locality') city = c.long_name
    else if (t === 'sublocality_level_1' && !city) city = c.long_name
    else if (t === 'administrative_area_level_1') state = c.short_name
    else if (t === 'postal_code') zip = c.long_name
  }
  return { address: [streetNumber, route].filter(Boolean).join(' '), city, state, zip }
}

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, inputStyle }) {
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)
  const [hoveredIdx, setHoveredIdx] = useState(-1)
  const [focused, setFocused] = useState(false)
  const [ready, setReady] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [typedAddress, setTypedAddress] = useState('')
  const [suggestedAddress, setSuggestedAddress] = useState(null)
  const svcRef = useRef(null)
  const tokenRef = useRef(null)
  const timerRef = useRef(null)
  const containerRef = useRef(null)
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_KEY

  useEffect(() => {
    if (!apiKey) return
    if (window.google?.maps?.places) { setReady(true); return }
    loadGoogleScript(apiKey).then(() => setReady(true)).catch(() => {})
  }, [apiKey])

  const fetchSuggestions = useCallback((input) => {
    if (!ready || !window.google?.maps?.places) return
    if (!svcRef.current) svcRef.current = new window.google.maps.places.AutocompleteService()
    if (!tokenRef.current) tokenRef.current = new window.google.maps.places.AutocompleteSessionToken()
    svcRef.current.getPlacePredictions(
      { input, types: ['address'], componentRestrictions: { country: 'us' }, sessionToken: tokenRef.current },
      (preds, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && preds?.length) {
          setSuggestions(preds.slice(0, 5))
          setOpen(true)
          setActiveIdx(-1)
        } else {
          setSuggestions([])
          setOpen(false)
        }
      }
    )
  }, [ready])

  const handleChange = (e) => {
    const v = e.target.value
    onChange(v)
    setShowConfirm(false)
    clearTimeout(timerRef.current)
    if (v.length >= 3 && ready) {
      timerRef.current = setTimeout(() => fetchSuggestions(v), 300)
    } else {
      setSuggestions([])
      setOpen(false)
    }
  }

  const selectSuggestion = (pred) => {
    const currentTyped = value.trim()
    const displayText = pred.structured_formatting?.main_text || pred.description || ''
    onChange(displayText)

    const ps = new window.google.maps.places.PlacesService(containerRef.current)
    ps.getDetails(
      { placeId: pred.place_id, fields: ['address_components', 'geometry'], sessionToken: tokenRef.current },
      (place, status) => {
        tokenRef.current = null
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.address_components) {
          const parsed = parseAddressComponents(place.address_components)
          const streetAddress = parsed.address || displayText || currentTyped
          const lat = place.geometry?.location?.lat?.() ?? null
          const lng = place.geometry?.location?.lng?.() ?? null
          const placeId = pred.place_id

          // EVERY suggestion goes through the confirm card — applying an address
          // is always an explicit user click (Suggested vs. What you typed). We
          // never auto-apply, no matter how close the suggestion is to the typed
          // value; that short-circuit was the silent address-overwrite path.
          setTypedAddress(currentTyped)
          setSuggestedAddress({ ...parsed, address: streetAddress, latitude: lat, longitude: lng, placeId })
          setShowConfirm(true)
          onChange(streetAddress)
        }
      }
    )
    setOpen(false)
    setSuggestions([])
    setHoveredIdx(-1)
  }

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault()
      selectSuggestion(suggestions[activeIdx])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // Pull marginBottom out of inputStyle so it goes on the wrapper div instead
  const { marginBottom, ...baseStyle } = inputStyle || {}
  const computedInput = {
    ...baseStyle,
    marginBottom: 0,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: open && suggestions.length > 0 ? '8px 8px 0 0' : '8px',
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: marginBottom ?? '14px' }}>
      <input
        style={computedInput}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          // Focus alone must NOT reopen a stale suggestion list — the dropdown
          // opens only as a result of typing (handleChange). This prevents an
          // accidental tap on a lingering suggestion from mutating the field.
          setFocused(true)
        }}
        onBlur={() => {
          setFocused(false)
          // Delay so mousedown on a suggestion fires before the list closes
          setTimeout(() => setOpen(false), 160)
        }}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
      />

      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1200,
          background: C.card,
          border: `1px solid ${C.amber}`, borderTop: `1px solid ${C.border}`,
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 8px 24px rgba(44,29,20,0.14)',
          overflow: 'hidden',
        }}>
          {suggestions.map((s, i) => {
            const isHighlighted = i === activeIdx || i === hoveredIdx
            return (
              <div
                key={s.place_id}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s) }}
                onTouchStart={(e) => { e.preventDefault(); selectSuggestion(s) }}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(-1)}
                style={{
                  padding: '10px 14px', cursor: 'pointer',
                  background: isHighlighted ? '#FBF3E8' : 'transparent',
                  borderBottom: i < suggestions.length - 1 ? `1px solid ${C.border}` : 'none',
                  transition: 'background 0.1s',
                }}
              >
                <div style={{
                  fontSize: '14px', color: C.brown, fontFamily: 'Calibri, sans-serif',
                  fontWeight: isHighlighted ? '600' : '400',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {s.structured_formatting.main_text}
                </div>
                <div style={{
                  fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '1px',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {s.structured_formatting.secondary_text}
                </div>
              </div>
            )
          })}
          <div style={{
            padding: '5px 14px', display: 'flex', justifyContent: 'flex-end',
            borderTop: `1px solid ${C.border}`, background: C.bg,
          }}>
            <span style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif', letterSpacing: '0.3px' }}>
              Powered by Google
            </span>
          </div>
        </div>
      )}

      {showConfirm && suggestedAddress && (
        <div style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: 16,
          marginTop: 8,
          boxShadow: '0 4px 16px rgba(44,29,20,0.1)',
        }}>
          <div style={{
            fontSize: 11, color: C.muted, fontWeight: 600,
            letterSpacing: 0.5, marginBottom: 12,
            fontFamily: 'Calibri, sans-serif',
          }}>
            CONFIRM YOUR ADDRESS
          </div>

          {/* Suggested */}
          <div
            onMouseDown={(e) => {
              e.preventDefault()
              onChange(suggestedAddress.address)
              onSelect(suggestedAddress)
              setShowConfirm(false)
            }}
            style={{
              padding: 12, borderRadius: 8,
              border: `2px solid ${C.amber}`,
              background: '#FFF8E7',
              cursor: 'pointer',
              marginBottom: 8,
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', marginBottom: 4,
            }}>
              <div style={{ fontSize: 11, color: C.amber, fontWeight: 600, letterSpacing: 0.5, fontFamily: 'Calibri, sans-serif' }}>
                ✓ SUGGESTED
              </div>
              <div style={{ fontSize: 10, color: C.amber, fontFamily: 'Calibri, sans-serif' }}>Recommended</div>
            </div>
            <div style={{ fontSize: 14, color: C.brown, fontWeight: 600, fontFamily: 'Georgia, serif' }}>
              {suggestedAddress.address}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 2, fontFamily: 'Calibri, sans-serif' }}>
              {[suggestedAddress.city, suggestedAddress.state, suggestedAddress.zip].filter(Boolean).join(', ')}
            </div>
          </div>

          {/* What they typed */}
          <div
            onMouseDown={(e) => {
              e.preventDefault()
              onChange(typedAddress)
              onSelect({
                address: typedAddress,
                city: suggestedAddress.city,
                state: suggestedAddress.state,
                zip: suggestedAddress.zip,
                latitude: suggestedAddress.latitude ?? null,
                longitude: suggestedAddress.longitude ?? null,
                placeId: suggestedAddress.placeId ?? null,
              })
              setShowConfirm(false)
            }}
            style={{
              padding: 12, borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: C.card,
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4, fontFamily: 'Calibri, sans-serif' }}>
              WHAT YOU TYPED
            </div>
            <div style={{ fontSize: 14, color: C.brown, fontFamily: 'Georgia, serif' }}>
              {typedAddress}
            </div>
          </div>

          <button
            onMouseDown={(e) => { e.preventDefault(); setShowConfirm(false) }}
            style={{
              background: 'none', border: 'none',
              color: C.muted, fontSize: 12,
              cursor: 'pointer', marginTop: 10,
              width: '100%', textAlign: 'center',
              fontFamily: 'Calibri, sans-serif',
            }}
          >
            Edit address manually
          </button>
        </div>
      )}
    </div>
  )
}
